// import router from '../router';
import securityManager from '../utils/security';
import { fetchWithAuth } from './authFetch';

// Base API URL from environment variable
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'https://api.dale365.com/api/v1';

/**
 * API service that handles all API requests and centralizes error handling
 * Specifically handles 403 Forbidden errors for IP blocking
 */
const apiService = {
  /**
   * Gets the token from localStorage
   * @returns {string|null} The authentication token or null if not logged in
   */
  getToken() {
    return localStorage.getItem('userToken');
  },

  /**
   * Builds headers for API requests, including authorization if token exists
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Object} Headers object for fetch request
   */
  getHeaders(additionalHeaders = {}) {
    const headers = {
      'Content-Type': 'application/json',
      'X-CSRF-Token': securityManager.getCSRFToken(),
      ...additionalHeaders
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }

    return headers;
  },

  /**
   * Handles 403 Forbidden errors from the API, managing IP blocking.
   *
   * IMPORTANT: Only treats the 403 as an IP-block signal when it comes from
   * an authentication endpoint (login, create-user, token-refresh). A 403
   * from a feature endpoint (matches, betting, etc.) is a permission error,
   * not an IP block, and must NOT wipe the user's session.
   *
   * @param {Response} response - The fetch response object
   * @param {string} [url] - Originating request URL; required to classify the 403.
   */
  async handleAccessDenied(response, url = '', requestConfig = {}) {
    if (response.status !== 403) return false;

    const AUTH_PATH_MARKERS = ['/login/', '/create-user/', '/token/refresh/'];
    const isAuthEndpoint = typeof url === 'string'
      && AUTH_PATH_MARKERS.some((marker) => url.includes(marker));
    if (!isAuthEndpoint) return false;

    try {
      // Clone response to read body without consuming the original
      const clonedResponse = response.clone();
      const text = await clonedResponse.text();
      let responseData;

      try {
        responseData = JSON.parse(text);
      } catch (e) {
        // Se não for JSON válido, não é nossa resposta específica
        return false;
      }

      // Verificar se corresponde EXATAMENTE ao formato {"detail": "Access denied."}
      if (responseData && responseData.detail === 'Access denied.') {
        console.warn('IP BLOQUEADO: Access denied detectado em endpoint de autenticação');

        // Limpar TODAS as credenciais de autenticação
        localStorage.removeItem('userToken');
        localStorage.removeItem('userLoggedIn');
        localStorage.removeItem('username');
        localStorage.removeItem('botPermissions');

        // Limpar cookies
        document.cookie.split(';').forEach(cookie => {
          const [name] = cookie.trim().split('=');
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });

        // Definir flag de IP bloqueado
        localStorage.setItem('ipBlocked', 'true');

        // Forçar redirecionamento para página de bloqueio
        window.location.href = '/blocked';
        return true;
      }
    } catch (e) {
      if (requestConfig.sensitive === true) {
        console.error('Sensitive API response processing failed');
      } else {
        console.error('Erro ao processar resposta de 403:', e);
      }
    }
    return false;
  },

  /**
   * Performs a fetch request with automatic handling of IP blocking (403 errors)
   * @param {string} url - The URL to fetch from
   * @param {Object} options - Fetch options object
   * @returns {Promise} The fetch promise
   */
  async fetchWithErrorHandling(url, options = {}, requestConfig = {}) {
    const sensitive = requestConfig.sensitive === true;
    // Verificar se o IP já está bloqueado antes de fazer qualquer requisição
    if (localStorage.getItem('ipBlocked') === 'true') {
      if (sensitive) {
        console.warn('Sensitive API request blocked');
      } else {
        console.warn('Tentativa de requisição com IP bloqueado:', url);
      }
      // Forçar redirecionamento para página de bloqueio
      window.location.href = '/blocked';
      // Simular erro de rede para falhar a requisição
      throw new Error('Network error: IP blocked');
    }
    
    try {
      const response = await fetchWithAuth(url, options);

      // Handle 403 Forbidden (Access denied) error for IP blocking.
      // Pass URL so the handler can restrict the wipe to auth endpoints only.
      if (await this.handleAccessDenied(response, url, requestConfig)) {
        throw new Error('IP blocked');
      }

      // Check if response is ok
      if (!response.ok) {
        if (sensitive) {
          console.error('Sensitive API request failed', {
            status: response.status,
            method: options.method
          });
        } else {
          console.error(`API Error for ${url}:`, {
            status: response.status,
            statusText: response.statusText,
            url: url,
            method: options.method
          });
        }

        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Check if response has content (status 204 returns no content)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        if (!sensitive) {
          console.log(`API Response for ${url}: No content (204)`);
        }
        return null;
      }
      
      // Parse JSON response
      const data = await response.json();
      if (!sensitive) {
        console.log(`API Response for ${url}:`, data);
      }
      return data;
    } catch (error) {
      // Check if error has a response object (probably from fetch)
      if (error.response && await this.handleAccessDenied(error.response, url, requestConfig)) {
        throw new Error('IP blocked');
      }
      throw error;
    }
  },

  /**
   * Performs a GET request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Promise} The fetch promise
   */
  async get(endpoint, additionalHeaders = {}, requestConfig = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const options = {
      method: 'GET',
      headers: this.getHeaders(additionalHeaders)
    };
    
    return this.fetchWithErrorHandling(url, options, requestConfig);
  },

  /**
   * Performs a POST request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Data to send
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Promise} The fetch promise
   */
  async post(endpoint, data, additionalHeaders = {}, requestConfig = {}) {
    // Sanitize data before sending
    const sanitizedData = this.sanitizeRequestData(data);
    
    const url = `${API_BASE_URL}${endpoint}`;
    const options = {
      method: 'POST',
      headers: this.getHeaders(additionalHeaders),
      body: JSON.stringify(sanitizedData)
    };
    
    return this.fetchWithErrorHandling(url, options, requestConfig);
  },

  /**
   * Performs a PUT request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Data to send
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Promise} The fetch promise
   */
  async put(endpoint, data, additionalHeaders = {}) {
    // Sanitize data before sending
    const sanitizedData = this.sanitizeRequestData(data);
    
    const url = `${API_BASE_URL}${endpoint}`;
    const options = {
      method: 'PUT',
      headers: this.getHeaders(additionalHeaders),
      body: JSON.stringify(sanitizedData)
    };
    
    return this.fetchWithErrorHandling(url, options);
  },

  /**
   * Performs a DELETE request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Promise} The fetch promise
   */
  async delete(endpoint, additionalHeaders = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const options = {
      method: 'DELETE',
      headers: this.getHeaders(additionalHeaders)
    };
    
    return this.fetchWithErrorHandling(url, options);
  },

  /**
   * Performs login with username and password
   * @param {string} username - User's username
   * @param {string} password - User's password
   * @returns {Promise} The fetch promise
   */
  async login(username, password) {
    // Sanitize inputs
    const sanitizedUsername = securityManager.sanitizeInput(username);
    const sanitizedPassword = password; // Don't sanitize passwords

    const url = `${API_BASE_URL}/login/`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': securityManager.getCSRFToken()
      },
      body: JSON.stringify({
        username: sanitizedUsername,
        password: sanitizedPassword
      })
    };

    // Use raw fetch for login — a 401 here means bad credentials,
    // not an expired token that should trigger refresh
    try {
      const response = await fetch(url, options);

      if (await this.handleAccessDenied(response, url)) {
        throw new Error('IP blocked');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return null;
      }

      return await response.json();
    } catch (error) {
      if (error.response && await this.handleAccessDenied(error.response, url)) {
        throw new Error('IP blocked');
      }
      throw error;
    }
  },

  /**
   * Sanitize request data recursively
   * @param {any} data - Data to sanitize
   * @returns {any} Sanitized data
   */
  sanitizeRequestData(data) {
    if (typeof data === 'string') {
      return securityManager.sanitizeInput(data);
    }
    
    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeRequestData(item));
    }
    
    if (data && typeof data === 'object') {
      const sanitized = {};
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          // Don't sanitize passwords or tokens
          if (key === 'password' || key === 'token' || key === 'csrf_token') {
            sanitized[key] = data[key];
          } else if (key === 'amount' || key === 'value' || key.includes('price')) {
            // Sanitize monetary values
            sanitized[key] = securityManager.sanitizeMonetaryValue(data[key]);
          } else {
            sanitized[key] = this.sanitizeRequestData(data[key]);
          }
        }
      }
      return sanitized;
    }
    
    return data;
  }
};

export default apiService;
